import React from 'react';
import usePreviousDistinct from 'react-use/lib/usePreviousDistinct';
import {GraphNode, GraphResponse} from '../interface';
import {UseQueryResult} from '@tanstack/react-query';

interface Props {
    queryGraphRequest: UseQueryResult<GraphResponse>;
    onLoaded: (nodes: GraphNode[], previousLoadedNodes?: GraphNode[]) => void;
}

export function useOnNodesLoaded({queryGraphRequest, onLoaded}: Props) {
    // tanstack query doesn't seem to have an option to fire a callback on every query data change,
    // only on data loaded from server, so I have to use this hack here, otherwise
    // this code wouldn't run on data loaded from cache

    const previousLoadedNodes = usePreviousDistinct(
        queryGraphRequest.data?.nodes,
        (prev, next) => !next
    );
    const previousLoadedNodesRef = React.useRef(previousLoadedNodes);

    previousLoadedNodesRef.current = previousLoadedNodes;
    const onNodesLoadedRef = React.useRef(onLoaded);

    onNodesLoadedRef.current = onLoaded;

    // this hack is used to block node structure request in cases when nodes were already loaded
    // but the state of selected nodes wasn't updated yet (bc react state update is async).
    // in this case there is exactly one render with new nodes data but still old selected nodes
    // which is enough for node structure request in factset panel to fail.
    // see 'fetch nodes structure request gets faulty params' test
    const requestCounterRef = React.useRef(0);
    const [requestedCounter, setReqeustedCounter] = React.useState(0);

    if (queryGraphRequest.data && queryGraphRequest.data.nodes !== previousLoadedNodes) {
        requestCounterRef.current += 1;
    }

    React.useEffect(() => {
        if (queryGraphRequest.data) {
            onNodesLoadedRef.current(queryGraphRequest.data.nodes, previousLoadedNodesRef.current);
            setReqeustedCounter((prev) => prev + 1);
        }
    }, [queryGraphRequest.data]);

    return {
        canFetchNodeStructure:
            !queryGraphRequest.isFetching && requestCounterRef.current === requestedCounter
    };
}
